import { OverlayModule } from '@angular/cdk/overlay';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { GroupedOperationsDto, OperationsApi, OperationsDto } from '@buspro/web/apis';
import { CacheKeysEnum, TenantRolesService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { lastValueFrom } from 'rxjs';
import { CommandPaletteService } from '../../services/command-palette.service';

const ACTIVE_OPERATIONS_ROLES: TenantUserRoles[] = [
  TenantUserRoles.TENANT_ADMIN,
  TenantUserRoles.TENANT_TERMINAL_MANAGER,
  TenantUserRoles.TENANT_PARCEL_MANAGER,
  TenantUserRoles.TERMINAL_BOOKING_CLERK,
];

export interface CommandPaletteItem {
  id: string;
  label: string;
  description?: string;
  icon: string;
  iconType?: 'fe' | 'fa';
  route?: string;
  queryParams?: Params;
  action?: () => void;
  category: string;
  keywords?: string[];
  roles?: TenantUserRoles[];
  /** Optional count for badge display (e.g. Active Operations) */
  count?: number;
}

export interface OperationCommandItem extends CommandPaletteItem {
  operationId: string;
  bookingProfileId: string;
  busName: string;
  routeName: string;
  departureTime: string;
  conductorName?: string;
  status: string;
}

@Component({
  selector: 'tenant-command-palette',
  standalone: true,
  imports: [CommonModule, NgClass, NgFor, NgIf, OverlayModule, NzInputModule, NzIconModule, NzBadgeModule, FormsModule],
  template: `
    <div
      class="command-palette-overlay"
      [class.visible]="isVisible()"
      (click)="close()"
    >
      <div class="command-palette-container" (click)="$event.stopPropagation()">
        <div class="command-palette-header">
          <span class="fe fe-search command-search-icon"></span>
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (input)="onSearchChange()"
            (keydown)="onKeyDown($event)"
            class="command-palette-input"
            placeholder="Search or jump to..."
            autofocus
            #searchInput
          />
          <kbd class="command-shortcut-hint">⌘K</kbd>
        </div>

        <div class="command-palette-results">
          <div *ngIf="allFlatItems().length === 0" class="command-palette-empty">
            <span class="fe fe-search text-4xl text-gray-400 mb-4"></span>
            <p class="text-gray-500">No results found</p>
          </div>

          <div *ngFor="let group of groupedItems(); trackBy: trackByCategory" class="command-group">
            <div class="command-group-header">{{ group.category }}</div>
            <div
              *ngFor="let item of group.items; trackBy: trackById"
              class="command-item"
              [class.is-operation]="isOperationItem(item)"
              [class.selected]="selectedIndex() === getItemIndex(item)"
              (click)="executeCommand(item)"
              (mouseenter)="selectedIndex.set(getItemIndex(item))"
            >
              <span
                [ngClass]="getItemIconClasses(item)"
                class="command-item-icon"
              ></span>
              <div class="command-item-content">
                <div class="command-item-label" [innerHTML]="highlightMatch(item.label, searchQuery())"></div>
                <div *ngIf="item.description" class="command-item-description">{{ item.description }}</div>
              </div>
              <nz-badge
                *ngIf="item.count !== undefined"
                [nzCount]="item.count"
                [nzOverflowCount]="99"
                class="command-item-badge"
              ></nz-badge>
              <kbd *ngIf="item.route" class="command-item-shortcut">↵</kbd>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .command-palette-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        z-index: 9999;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 10vh;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--transition-base);

        &.visible {
          opacity: 1;
          pointer-events: all;
        }
      }

      .command-palette-container {
        width: 90%;
        max-width: 640px;
        background: var(--header-bg);
        border-radius: 12px;
        box-shadow: var(--shadow-lg);
        border: 1px solid var(--header-border);
        overflow: hidden;
        transform: scale(0.95);
        transition: transform var(--transition-base);

        .command-palette-overlay.visible & {
          transform: scale(1);
        }
      }

      .command-palette-header {
        display: flex;
        align-items: center;
        padding: 16px;
        border-bottom: 1px solid var(--header-border);
        gap: 12px;

        .command-search-icon {
          color: var(--header-search-icon);
          font-size: 20px;
          flex-shrink: 0;
        }

        .command-palette-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          color: var(--header-text);
          font-size: 16px;
          padding: 0;

          &::placeholder {
            color: var(--header-text-secondary);
          }
        }

        .command-shortcut-hint {
          background: var(--card-hover);
          border: 1px solid var(--card-border);
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 12px;
          color: var(--card-text-secondary);
          font-family: monospace;
        }
      }

      .command-palette-results {
        max-height: 400px;
        overflow-y: auto;
        padding: 8px;

        .command-palette-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 24px;
          text-align: center;
        }
      }

      .command-group {
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        .command-group-header {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--card-text-secondary);
          padding: 8px 12px;
          margin-bottom: 4px;
        }
      }

      .command-item {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        transition: all var(--transition-fast);
        gap: 12px;

        &:hover,
        &.selected {
          background-color: rgba(0, 114, 188, 0.08);
        }

        .command-item-icon {
          font-size: 18px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--card-text-secondary);
          flex-shrink: 0;
        }

        .command-item-content {
          flex: 1;
          min-width: 0;

          .command-item-label {
            font-size: 14px;
            font-weight: 500;
            color: var(--card-text);
            margin-bottom: 2px;

            ::ng-deep .highlight {
              background-color: var(--accent-primary);
              color: white;
              padding: 0 2px;
              border-radius: 2px;
            }
          }

          .command-item-description {
            font-size: 12px;
            color: var(--card-text-secondary);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .command-item-shortcut {
          background: var(--card-hover);
          border: 1px solid var(--card-border);
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 11px;
          color: var(--card-text-secondary);
          font-family: monospace;
        }

        .command-item-badge {
          margin-left: auto;
        }

        &.is-operation {
          padding-left: 24px;

          .command-item-label {
            font-weight: 600;
            color: var(--card-text);
          }

          .command-item-description {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
          }
        }

        .status-dot {
          position: absolute;
          top: 2px;
          right: 2px;
          width: 6px;
          height: 6px;
          border-radius: 50%;

          &.loading {
            background-color: #fbbf24;
            animation: pulse 2s infinite;
          }

          &.in-transit {
            background-color: #22c55e;
            animation: pulse 2s infinite;
          }
        }
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `,
  ],
})
export class CommandPaletteComponent implements OnInit {
  private commandPaletteService = inject(CommandPaletteService);
  readonly isVisible = this.commandPaletteService.isOpen;
  searchQuery = signal('');
  selectedIndex = signal(0);
  items: CommandPaletteItem[] = [];
  private roleService = inject(TenantRolesService);
  private router = inject(Router);
  private operationsApi = inject(OperationsApi);
  private ngxAuthService = inject(NgxAuthService);

  private context = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);
  private refetchInterval = 7000;

  authUser = toSignal(this.ngxAuthService.state, { initialValue: null });

  showActiveOperationsSection = computed(() => {
    const user = this.authUser();
    if (!user || !user.role) return false;
    return ACTIVE_OPERATIONS_ROLES.includes(user.role as TenantUserRoles);
  });

  loadingOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.LOADING_OPERATIONS_GROUPED_BY_DATE, 'palette-loading'],
    queryFn: () =>
      lastValueFrom(this.operationsApi.operationsControllerGetLoadingOperationGroupedByDate(undefined, this.context)),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
    enabled: this.isVisible() && this.showActiveOperationsSection(),
  }));

  inTransitOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.IN_TRANSIT_OPERATIONS_GROUPED_BY_DATE, 'palette-in-transit'],
    queryFn: () =>
      lastValueFrom(this.operationsApi.operationsControllerGetInTransitOperationGroupedByDate(undefined, this.context)),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
    enabled: this.isVisible() && this.showActiveOperationsSection(),
  }));

  finalizingOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.FINALIZING_OPERATIONS_GROUPED_BY_DATE, 'palette-finalizing'],
    queryFn: () =>
      lastValueFrom(
        this.operationsApi.operationsControllerGetFinalizingOperationGroupedByDate(undefined, this.context),
      ),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
    enabled: this.isVisible() && this.showActiveOperationsSection(),
  }));

  loadingCount = computed(() => {
    const data = this.loadingOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  inTransitCount = computed(() => {
    const data = this.inTransitOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  finalizingCount = computed(() => {
    const data = this.finalizingOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  private flattenAndSortOperations(groups: GroupedOperationsDto[]): OperationsDto[] {
    const allOps = groups.flatMap((group) => group.operations || []);
    return allOps.sort((a, b) => new Date(b.departureTime).getTime() - new Date(a.departureTime).getTime());
  }

  private formatTime(iso: string): string {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  topLoadingOperations = computed((): OperationCommandItem[] => {
    const data = this.loadingOperationsQuery.data();
    if (!data || !this.showActiveOperationsSection()) return [];
    const sorted = this.flattenAndSortOperations(data);
    return sorted.slice(0, 3).map((op) => ({
      id: `op-loading-${op.id}`,
      label: `${op.busName} • ${op.routeName}`,
      description: `Loading • ${this.formatTime(op.departureTime)} • ${op.conductorName}`,
      icon: 'fe-truck',
      route: `/booking/profile/${op.bookingProfile.id}`,
      category: 'Loading',
      operationId: op.id,
      bookingProfileId: op.bookingProfile.id,
      busName: op.busName,
      routeName: op.routeName,
      departureTime: op.departureTime,
      conductorName: op.conductorName,
      status: op.status,
      keywords: [op.busName, op.routeName, op.conductorName ?? '', op.bookingProfile.id],
    }));
  });

  topInTransitOperations = computed((): OperationCommandItem[] => {
    const data = this.inTransitOperationsQuery.data();
    if (!data || !this.showActiveOperationsSection()) return [];
    const sorted = this.flattenAndSortOperations(data);
    return sorted.slice(0, 3).map((op) => ({
      id: `op-in-transit-${op.id}`,
      label: `${op.busName} • ${op.routeName}`,
      description: `In Transit • ${this.formatTime(op.departureTime)} • ${op.conductorName}`,
      icon: 'fe-activity',
      route: `/booking/profile/${op.bookingProfile.id}`,
      category: 'In Transit',
      operationId: op.id,
      bookingProfileId: op.bookingProfile.id,
      busName: op.busName,
      routeName: op.routeName,
      departureTime: op.departureTime,
      conductorName: op.conductorName,
      status: op.status,
      keywords: [op.busName, op.routeName, op.conductorName ?? '', op.bookingProfile.id],
    }));
  });

  topFinalizingOperations = computed((): OperationCommandItem[] => {
    const data = this.finalizingOperationsQuery.data();
    if (!data || !this.showActiveOperationsSection()) return [];
    const sorted = this.flattenAndSortOperations(data);
    return sorted.slice(0, 3).map((op) => ({
      id: `op-finalizing-${op.id}`,
      label: `${op.busName} • ${op.routeName}`,
      description: `Finalizing • ${this.formatTime(op.departureTime)} • ${op.conductorName}`,
      icon: 'fe-check-circle',
      route: `/booking/profile/${op.bookingProfile.id}`,
      category: 'Finalizing',
      operationId: op.id,
      bookingProfileId: op.bookingProfile.id,
      busName: op.busName,
      routeName: op.routeName,
      departureTime: op.departureTime,
      conductorName: op.conductorName,
      status: op.status,
      keywords: [op.busName, op.routeName, op.conductorName ?? '', op.bookingProfile.id],
    }));
  });

  activeOperationsItems = computed((): CommandPaletteItem[] => {
    if (!this.showActiveOperationsSection()) return [];

    const query = this.searchQuery().toLowerCase().trim();
    if (query) {
      // When searching, inline operations are handled via filteredItems
      return [];
    }

    const loading = this.loadingCount();
    const inTransit = this.inTransitCount();
    const finalizing = this.finalizingCount();

    const items: CommandPaletteItem[] = [];

    if (loading > 0) {
      items.push({
        id: 'active-ops-loading-header',
        label: `Loading (${loading})`,
        description: 'Operations loading at terminal',
        icon: 'fe-truck',
        route: '/operations/active',
        queryParams: { tab: 'loading' },
        category: 'Active Operations',
        count: loading,
      });
      items.push(...this.topLoadingOperations());
      if (loading > 3) {
        items.push({
          id: 'active-ops-loading-view-all',
          label: `View all ${loading} loading operations`,
          icon: 'fe-external-link',
          route: '/operations/active',
          queryParams: { tab: 'loading' },
          category: 'Active Operations',
        });
      }
    }

    if (inTransit > 0) {
      items.push({
        id: 'active-ops-in-transit-header',
        label: `In Transit (${inTransit})`,
        description: 'Operations in transit',
        icon: 'fe-activity',
        route: '/operations/active',
        queryParams: { tab: 'in-transit' },
        category: 'Active Operations',
        count: inTransit,
      });
      items.push(...this.topInTransitOperations());
      if (inTransit > 3) {
        items.push({
          id: 'active-ops-in-transit-view-all',
          label: `View all ${inTransit} in-transit operations`,
          icon: 'fe-external-link',
          route: '/operations/active',
          queryParams: { tab: 'in-transit' },
          category: 'Active Operations',
        });
      }
    }

    if (finalizing > 0) {
      items.push({
        id: 'active-ops-finalizing-header',
        label: `Finalizing (${finalizing})`,
        description: 'Arrived, handover, verification',
        icon: 'fe-check-circle',
        route: '/operations/active',
        queryParams: { tab: 'finalizing' },
        category: 'Active Operations',
        count: finalizing,
      });
      items.push(...this.topFinalizingOperations());
      if (finalizing > 3) {
        items.push({
          id: 'active-ops-finalizing-view-all',
          label: `View all ${finalizing} finalizing operations`,
          icon: 'fe-external-link',
          route: '/operations/active',
          queryParams: { tab: 'finalizing' },
          category: 'Active Operations',
        });
      }
    }

    if (!loading && !inTransit && !finalizing) {
      items.push({
        id: 'active-ops-empty',
        label: 'No active operations',
        description: 'You have no loading or in-progress operations right now',
        icon: 'fe-info',
        category: 'Active Operations',
      });
    }

    return items;
  });

  allFlatItems = computed(() => {
    const active = this.activeOperationsItems();
    const filtered = this.filteredItems();
    if (active.length > 0) {
      return [...active, ...filtered];
    }
    return filtered;
  });

  filteredItems = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.items;
    }

    const allOperations: CommandPaletteItem[] = [
      ...this.topLoadingOperations(),
      ...this.topInTransitOperations(),
      ...this.topFinalizingOperations(),
    ];

    const combinedItems = [...allOperations, ...this.items];

    return combinedItems.filter((item) => {
      const searchableText = [item.label, item.description, item.category, ...(item.keywords || [])]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  });

  groupedItems = computed(() => {
    const activeItems = this.activeOperationsItems();
    const filtered = this.filteredItems();
    const userRole = this.authUser()?.role as TenantUserRoles | undefined;
    const result: { category: string; items: CommandPaletteItem[] }[] = [];

    if (activeItems.length > 0) {
      result.push({ category: 'Active Operations', items: activeItems });
    }

    const groups = new Map<string, CommandPaletteItem[]>();
    filtered.forEach((item) => {
      if (item.roles?.length && (!userRole || !item.roles.includes(userRole))) {
        return;
      }
      if (!groups.has(item.category)) {
        groups.set(item.category, []);
      }
      groups.get(item.category)!.push(item);
    });
    result.push(
      ...Array.from(groups.entries()).map(([category, items]) => ({
        category,
        items,
      })),
    );

    return result;
  });

  ngOnInit() {
    this.loadCommands();
  }

  open() {
    this.commandPaletteService.open();
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  close() {
    this.commandPaletteService.close();
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  toggle() {
    this.commandPaletteService.toggle();
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Cmd+K or Ctrl+K to open
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
      return;
    }

    // Escape to close
    if (event.key === 'Escape' && this.isVisible()) {
      event.preventDefault();
      this.close();
      return;
    }

    // Only handle navigation if palette is visible
    if (!this.isVisible()) {
      return;
    }

    const items = this.allFlatItems();
    const lastIndex = items.length - 1;

    // Arrow keys for navigation
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (lastIndex < 0) return;
      this.selectedIndex.set(Math.min(this.selectedIndex() + 1, lastIndex));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (lastIndex < 0) return;
      this.selectedIndex.set(Math.max(this.selectedIndex() - 1, 0));
      return;
    }

    // Enter to execute
    if (event.key === 'Enter') {
      event.preventDefault();
      if (lastIndex < 0) return;
      const selectedItem = items[this.selectedIndex()];
      if (selectedItem) {
        this.executeCommand(selectedItem);
      }
      return;
    }
  }

  onSearchChange() {
    this.selectedIndex.set(0);
  }

  onKeyDown(_event: KeyboardEvent) {
    // Prevent default behavior is handled in handleKeyboardEvent
  }

  executeCommand(item: CommandPaletteItem) {
    if (item.route) {
      if (item.queryParams) {
        this.router.navigate([item.route], { queryParams: item.queryParams });
      } else {
        this.router.navigate([item.route]);
      }
    }
    if (item.action) {
      item.action();
    }
    this.close();
  }

  getItemIndex(item: CommandPaletteItem): number {
    const items = this.allFlatItems();
    return items.findIndex((i) => i.id === item.id);
  }

  trackByCategory(_index: number, group: { category: string; items: CommandPaletteItem[] }): string {
    return group.category;
  }

  trackById(_index: number, item: CommandPaletteItem): string {
    return item.id;
  }

  highlightMatch(text: string, query: string): string {
    if (!query) {
      return text;
    }

    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  getItemIconClasses(item: CommandPaletteItem): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    if (item.iconType !== 'fa') {
      classes['fe'] = true;
    } else {
      classes['fa'] = true;
    }
    classes[item.icon] = true;
    return classes;
  }

  isOperationItem(item: CommandPaletteItem): boolean {
    return (item as OperationCommandItem).operationId !== undefined;
  }

  private loadCommands() {
    // Comprehensive command list covering all major features
    this.items = [
      // Insights
      {
        id: 'dashboard',
        label: 'Dashboard',
        description: 'View dashboard overview and analytics',
        icon: 'fe-bar-chart',
        route: '/dashboard',
        category: 'Insights',
        keywords: ['dashboard', 'overview', 'home', 'analytics', 'stats'],
      },
      {
        id: 'monitor',
        label: 'Monitor',
        description: 'Monitor operations and vehicles in real-time',
        icon: 'fa-dashboard',
        iconType: 'fa',
        route: '/monitor',
        category: 'Insights',
        keywords: ['monitor', 'tracking', 'vehicles', 'real-time', 'live'],
      },
      // Operations - Buses
      {
        id: 'available-buses',
        label: 'Available Buses',
        description: 'View and manage available buses',
        icon: 'fa-bus',
        iconType: 'fa',
        route: '/operations/available-buses',
        category: 'Operations',
        keywords: ['buses', 'fleet', 'vehicles', 'available'],
      },
      {
        id: 'terminal-available-buses',
        label: 'Terminal Available Buses',
        description: 'View buses available at terminal',
        icon: 'fa-bus',
        iconType: 'fa',
        route: '/operations/terminal-available-buses',
        category: 'Operations',
        keywords: ['buses', 'terminal', 'available'],
      },
      // Operations - Bookings
      {
        id: 'terminal-live-bookings',
        label: 'Terminal Live Bookings',
        description: 'View live bookings at terminal',
        icon: 'fa-building-o',
        iconType: 'fa',
        route: '/operations/terminal-live-bookings',
        category: 'Operations',
        keywords: ['bookings', 'terminal', 'live', 'tickets'],
      },
      {
        id: 'bus-stop-live-bookings',
        label: 'Bus Stop Live Bookings',
        description: 'View live bookings at bus stops',
        icon: 'fe-map-pin',
        route: '/operations/bus-stop-live-bookings',
        category: 'Operations',
        keywords: ['bookings', 'bus stop', 'live', 'tickets'],
      },
      {
        id: 'future-bookings',
        label: 'Future Bookings',
        description: 'View and manage future bookings',
        icon: 'fe-calendar',
        route: '/booking/futures',
        category: 'Operations',
        keywords: ['bookings', 'future', 'upcoming', 'scheduled'],
      },
      {
        id: 'ticket-search',
        label: 'Ticket Search',
        description: 'Search for tickets and bookings',
        icon: 'fe-check-square',
        route: '/booking/tickets-search',
        category: 'Operations',
        keywords: ['tickets', 'search', 'find', 'booking'],
      },
      // Operations - Schedules
      {
        id: 'schedules',
        label: 'Schedules',
        description: 'Manage departure schedules',
        icon: 'fa-sliders',
        iconType: 'fa',
        route: '/operations/schedules',
        category: 'Operations',
        keywords: ['schedules', 'departures', 'times', 'routes'],
      },
      {
        id: 'terminal-schedules',
        label: 'Terminal Schedules',
        description: 'Manage terminal-specific schedules',
        icon: 'fa-sliders',
        iconType: 'fa',
        route: '/operations/schedules/terminal',
        category: 'Operations',
        keywords: ['schedules', 'terminal', 'departures'],
      },
      // Operations - Active & Completed
      {
        id: 'ended-trips',
        label: 'Ended Trips',
        description: 'View completed and ended trips',
        icon: 'fe-bookmark',
        route: '/operations/completed',
        category: 'Operations',
        keywords: ['ended', 'completed', 'trips', 'finished', 'history'],
      },
      // Operations - Financial
      {
        id: 'refunds',
        label: 'Refunds',
        description: 'Manage refunds and cancellations',
        icon: 'fe-dollar-sign',
        route: '/refunds',
        category: 'Operations',
        keywords: ['refunds', 'money', 'cancel', 'payment'],
      },
      {
        id: 'reports',
        label: 'Reports',
        description: 'View and generate reports',
        icon: 'fe-file-text',
        route: '/reports',
        category: 'Operations',
        keywords: ['reports', 'analytics', 'data', 'export'],
        // Mirrors the server's REPORT_READERS: clerks read no company money.
        roles: [
          TenantUserRoles.TENANT_ADMIN,
          TenantUserRoles.TENANT_DIRECTOR,
          TenantUserRoles.TENANT_MANAGEMENT_MEMBER,
          TenantUserRoles.TENANT_ACCOUNTANT,
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          TenantUserRoles.TENANT_PARCEL_MANAGER,
        ],
      },
      // Parcels
      {
        id: 'parcels-received',
        label: 'Received Parcels',
        description: 'View received parcels',
        icon: 'fe-inbox',
        route: '/parcels/received',
        category: 'Parcels',
        keywords: ['parcels', 'received', 'inbox', 'incoming'],
      },
      {
        id: 'parcels-pickup',
        label: 'Ready for Pickup',
        description: 'View parcels ready for pickup',
        icon: 'fe-package',
        route: '/parcels/pickup',
        category: 'Parcels',
        keywords: ['parcels', 'pickup', 'ready', 'collection'],
      },
      {
        id: 'parcels-delivered',
        label: 'Delivered Parcels',
        description: 'View delivered parcels',
        icon: 'fe-check-circle',
        route: '/parcels/delivered',
        category: 'Parcels',
        keywords: ['parcels', 'delivered', 'completed', 'done'],
      },
      {
        id: 'parcels-manifests',
        label: 'Delivery Manifests',
        description: 'View delivery manifests',
        icon: 'fe-award',
        route: '/parcels/deliveries',
        category: 'Parcels',
        keywords: ['parcels', 'manifests', 'delivery', 'shipping'],
      },
      {
        id: 'parcels-search',
        label: 'Search Parcels',
        description: 'Search for parcels',
        icon: 'fe-search',
        route: '/parcels/search',
        category: 'Parcels',
        keywords: ['parcels', 'search', 'find', 'lookup'],
      },
      {
        id: 'parcels-missing',
        label: 'Missing Parcels',
        description: 'View missing parcels',
        icon: 'fe-alert-circle',
        route: '/parcels/missing',
        category: 'Parcels',
        keywords: ['parcels', 'missing', 'lost', 'alert'],
      },
      {
        id: 'parcels-collections',
        label: 'Parcel Collections',
        description: 'View parcel collections',
        icon: 'fa-cubes',
        iconType: 'fa',
        route: '/parcels',
        category: 'Parcels',
        keywords: ['parcels', 'collections', 'grouped'],
      },
      {
        id: 'parcel-item-types',
        label: 'Parcel Item Types',
        description: 'Manage parcel item types and categories',
        icon: 'fe-package',
        route: '/parcels/parcel-item-types',
        category: 'Parcels',
        keywords: ['parcels', 'types', 'categories', 'settings'],
      },
      // Routes
      {
        id: 'terminals',
        label: 'Terminals',
        description: 'Manage terminals',
        icon: 'fa-building-o',
        iconType: 'fa',
        route: '/terminals',
        category: 'Routes',
        keywords: ['terminals', 'stations', 'locations'],
      },
      {
        id: 'terminal-routes',
        label: 'Terminal Routes',
        description: 'Manage terminal routes',
        icon: 'fe-map',
        route: '/routes/terminal',
        category: 'Routes',
        keywords: ['routes', 'terminal', 'paths'],
      },
      {
        id: 'bus-stops',
        label: 'Bus Stops',
        description: 'Manage bus stops',
        icon: 'fe-stop-circle',
        route: '/bus-stops',
        category: 'Routes',
        keywords: ['bus stops', 'stops', 'stations', 'locations'],
      },
      {
        id: 'routes',
        label: 'Routes',
        description: 'Manage bus routes',
        icon: 'fe-map',
        route: '/routes',
        category: 'Routes',
        keywords: ['routes', 'paths', 'directions'],
      },
      // Fleet
      {
        id: 'buses',
        label: 'Buses',
        description: 'Manage bus fleet',
        icon: 'fa-bus',
        iconType: 'fa',
        route: '/buses',
        category: 'Fleet',
        keywords: ['buses', 'fleet', 'vehicles', 'assets'],
      },
      {
        id: 'my-assigned-buses',
        label: 'My Assigned Buses',
        description: 'View your assigned buses',
        icon: 'fa-bus',
        iconType: 'fa',
        route: '/buses/conductor/1',
        category: 'Fleet',
        keywords: ['buses', 'assigned', 'my', 'conductor'],
      },
      {
        id: 'conductors',
        label: 'Conductors',
        description: 'Manage conductors',
        icon: 'fe-user-x',
        route: '/conductors',
        category: 'Fleet',
        keywords: ['conductors', 'staff', 'employees', 'personnel'],
      },
      {
        id: 'drivers',
        label: 'Drivers',
        description: 'Manage drivers',
        icon: 'fe-user-check',
        route: '/drivers',
        category: 'Fleet',
        keywords: ['drivers', 'staff', 'employees', 'personnel'],
      },
      // Access Control
      {
        id: 'users',
        label: 'Users',
        description: 'Manage users and access control',
        icon: 'fe-shield',
        route: '/access-control',
        category: 'Access Control',
        keywords: ['users', 'access', 'control', 'permissions', 'roles'],
      },
      {
        id: 'billing',
        label: 'Billing',
        description: 'View billing and invoices',
        icon: 'fe-credit-card',
        route: '/billing',
        category: 'Administration',
        keywords: ['billing', 'invoices', 'payments', 'subscription'],
        roles: [TenantUserRoles.TENANT_ADMIN],
      },
      // Quick Actions
      {
        id: 'new-booking',
        label: 'New Booking',
        description: 'Create a new booking',
        icon: 'fe-calendar-plus',
        route: '/booking/new',
        category: 'Quick Actions',
        keywords: ['booking', 'new', 'create', 'add'],
      },
      {
        id: 'receive-parcel',
        label: 'Receive Parcel',
        description: 'Receive a new parcel',
        icon: 'fe-package',
        route: '/parcels/receive',
        category: 'Quick Actions',
        keywords: ['parcel', 'receive', 'new', 'add'],
      },
      {
        id: 'new-schedule',
        label: 'New Schedule',
        description: 'Create a new schedule',
        icon: 'fe-calendar',
        route: '/operations/schedules/new',
        category: 'Quick Actions',
        keywords: ['schedule', 'new', 'create', 'add'],
      },
      {
        id: 'add-bus',
        label: 'Add Bus',
        description: 'Add a new bus to the fleet',
        icon: 'fa-bus',
        iconType: 'fa',
        route: '/buses',
        category: 'Quick Actions',
        keywords: ['bus', 'new', 'create', 'add', 'fleet'],
      },
      // Settings & Profile
      {
        id: 'profile',
        label: 'My Profile',
        description: 'View and edit your profile',
        icon: 'fe-user',
        route: '/profile',
        category: 'Settings',
        keywords: ['profile', 'account', 'settings', 'me'],
      },
      {
        id: 'notifications',
        label: 'Notifications',
        description: 'View all notifications',
        icon: 'fe-bell',
        route: '/notification-center',
        category: 'Settings',
        keywords: ['notifications', 'alerts', 'messages'],
      },
    ];
  }
}
